import { SectionHeader } from "./SectionHeader";
import { MapPin, Car, Ship, Bus } from "lucide-react";
import useReveal from "@/lib/useReveal";
import { useSection } from "@/lib/SiteContent";
import { imageUrl } from "@/lib/api";

const DEFAULT_IMG =
  "https://images.unsplash.com/photo-1761095596674-546f146fa03d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZWxlZ2FudCUyMHdlZGRpbmclMjB0YWJsZSUyMHNldHRpbmd8ZW58MHx8fHwxNzc4NDI3OTY4fDA&ixlib=rb-4.1.0&q=85";

const FALLBACK = {
  subtitle: "",
  time_label: "",
  heading: "",
  address: "",
  by_car: "",
  by_boat: "",
  by_bus: "Rute 360 fra Fredrikstad busstasjon går direkte til Skjærhalden. Vi ordner med fellestransport fra Fredrikstad — gi oss beskjed om du trenger det.",
  maps_url: "#",
  image_id: null,
};

export const Location = () => {
  const loc = useSection("location", FALLBACK);
  const img = loc.image_id ? imageUrl(loc.image_id) : DEFAULT_IMG;
  const refImg = useReveal();
  const refText = useReveal();

  return (
    <section
      id="sted"
      data-testid="location-section"
      className="py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#F3F5E7]"
    >
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Kapittel III"
          title="Spjærøy, Hvaler"
          subtitle={loc.subtitle}
          testId="location"
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div ref={refText} className="reveal lg:col-span-6 lg:pr-8">
            <p className="text-xs uppercase tracking-[0.3em] text-[#68919B] mb-6">{loc.time_label}</p>
            <h3 className="font-serif text-3xl md:text-4xl text-[#3D5E68] font-light leading-tight">
              {loc.heading}
            </h3>
            <div className="mt-12 space-y-8">
              <div className="flex gap-5">
                <MapPin size={20} strokeWidth={1.3} className="text-[#68919B] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#5A7682] mb-1">Adresse</p>
                  <p className="text-[#3D5E68] font-light">{loc.address}</p>
                </div>
              </div>
              <div className="flex gap-5">
                <Car size={20} strokeWidth={1.3} className="text-[#68919B] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#5A7682] mb-1">Med bil</p>
                  <p className="text-[#3D5E68] font-light">{loc.by_car}</p>
                </div>
              </div>
              <div className="flex gap-5">
                <Ship size={20} strokeWidth={1.3} className="text-[#68919B] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#5A7682] mb-1">Med båt</p>
                  <p className="text-[#3D5E68] font-light">{loc.by_boat}</p>
                </div>
              </div>
              <div className="flex gap-5">
                <Bus size={20} strokeWidth={1.3} className="text-[#68919B] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#5A7682] mb-1">Med buss</p>
                  <p className="text-[#3D5E68] font-light">{loc.by_bus}</p>
                </div>
              </div>
            </div>
            <a
              href={loc.maps_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-maps-link"
              className="inline-block mt-12 px-8 py-3 border border-[#3D5E68] text-xs uppercase tracking-[0.25em] text-[#3D5E68] hover:bg-[#3D5E68] hover:text-[#F3F5E7] transition-colors duration-500"
            >
              Åpne i Google Maps
            </a>
          </div>
          <div ref={refImg} className="reveal lg:col-span-6">
            <div className="img-zoom aspect-[4/5] lg:aspect-[3/4]">
              <img src={img} alt="Bord ved havet" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
